import { createContext, useContext, type ReactNode } from "react";
import { editorText, type EditorText, type EditorTextKey } from "./editor-text";
import type {
  FontFaceDescriptor,
  Project,
  DamProviderInfo,
  PlacedPdf,
} from "@proof/core";
import type {
  FontLoader,
  FormGeneratorProps,
  ImageTransformer,
  AssetLoader,
} from "@proof/ui";
export interface EditorPresentation {
  /** Presentation controls only; server authorization remains authoritative. */
  enableStatements?: boolean;
  enableRules?: boolean;
  enableFormatting?: boolean;
  enableValidation?: boolean;
  enableDynamicSources?: boolean;
  enableConditionalVisibility?: boolean;
  enablePartOverrides?: boolean;
  /** Field settings sections; each can be hidden independently. */
  enableFormDisplay?: boolean;
  /** Integration entry points: Advanced setup and JSON configuration. */
  enableAdvancedSetup?: boolean;
  enableJsonConfiguration?: boolean;
  /** Show the System / Calculated / Required filters above the field list. */
  enableFieldFilters?: boolean;
  /** Offer "Use Source Input" so sources can come from the host page. */
  enableSourceInputs?: boolean;
  /** Initial filter states; each defaults to false (no filtering). */
  fieldFilters?: { system?: boolean; calculated?: boolean; required?: boolean };
  enableCalculatedValues?: boolean;
  /** Existing editor areas; each stays on unless set to false. */
  enableFormLayout?: boolean;
  enableFormGeneration?: boolean;
  enableFormPreview?: boolean;
  enableGlobalForms?: boolean;
  enableDataPresets?: boolean;
  allowProjectRename?: boolean;
  showHeading?: boolean;
  showDescription?: boolean;
  /** Replacement wording for headings, descriptions, labels and actions. */
  text?: EditorText;
  /**
   * Exact-match replacements for any visible label, button, toggle or dialog
   * title, keyed by the default English wording (e.g. {"Add field": "New input"}).
   */
  labels?: Record<string, string>;
}
export interface AuthoringServices {
  /** The host owns impersonation, popup authorization and audit attribution. */
  tryItOut?: (project: Project, signal: AbortSignal) => void | Promise<void>;
  /** Host-owned "Test this form": opens the project in a separate test window (version/draft/form choice). */
  testForm?: (project: Project) => void;
  editorPresentation?: EditorPresentation;
  listFormProjects?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<{ id: string; name: string }[]>;
  loadFormProject?: (
    id: string,
    signal: AbortSignal,
  ) => Promise<Project | undefined>;
  listFormProfiles?: (
    query: string,
    signal: AbortSignal,
    project?: Project,
  ) => Promise<
    | { id: string; name: string; values: import("@proof/core").Values }[]
    | import("@proof/core").HostQueryResult
  >;
  /** Profile/part callbacks may return rows or `{ data, translate }` (§ host queries). */
  listPartOverrides?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<
    | { id: string; name: string; sku?: string }[]
    | import("@proof/core").HostQueryResult
  >;
  loadPartOverride?: (
    id: string,
    signal: AbortSignal,
  ) => Promise<
    import("@proof/core").Part | import("@proof/core").HostQueryResult
  >;
  searchPartSkus?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<
    | { id: string; name: string; sku?: string }[]
    | import("@proof/core").HostQueryResult
  >;

  listFieldPresets?: (
    query?: import("@proof/core").FieldPresetQuery,
  ) => Promise<import("@proof/core").FieldPresetCatalog>;
  saveFieldPreset?: (
    input: import("@proof/core").FieldPresetWrite,
  ) => Promise<import("@proof/core").FieldPreset>;
  profileDataset?: (
    resource: import("@proof/core").Resource,
  ) => Promise<import("@proof/core").ProfileDatasetView>;
  saveProfileDataset?: (
    input: import("@proof/core").ProfileDatasetWrite,
    resource: import("@proof/core").Resource,
  ) => Promise<import("@proof/core").ProfileDatasetView>;
  listResources?: () => Promise<import("@proof/core").Resource[]>;
  saveResource?: (
    resource: import("@proof/core").Resource,
  ) => Promise<import("@proof/core").Resource>;
  removeBackground?: FormGeneratorProps["removeBackground"];
  dam?: (
    project: Project,
    partId?: string,
  ) => NonNullable<FormGeneratorProps["dam"]>;
  listDamProviders?: () => Promise<DamProviderInfo[]>;
  loadAsset?: AssetLoader;
  importBase?: (file: File, signal: AbortSignal) => Promise<Project>;
  importPdfArtwork?: (
    file: File,
    page: number,
    signal: AbortSignal,
  ) => Promise<PlacedPdf>;
  transformImage?: ImageTransformer;
  maps?: FormGeneratorProps["maps"];
  listFonts?: () => Promise<FontFaceDescriptor[]>;
  loadFont?: FontLoader;
  assetUrl?: (id: string) => string;
  upload?: FormGeneratorProps["upload"];
  assets?: FormGeneratorProps["assets"];
  listAssets?: (
    signal?: AbortSignal,
  ) => Promise<NonNullable<FormGeneratorProps["assets"]>>;
  onProofStatus?: (status: { ready: boolean; error?: string }) => void;
}
const defaults: AuthoringServices = {
  loadFont: async () => {
    throw new Error("Provide a font loader for managed font faces");
  },
  assetUrl: () =>
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
};
const Services = createContext(defaults);
export function AuthoringServicesProvider({
  services,
  children,
}: {
  services?: AuthoringServices;
  children: ReactNode;
}) {
  const inherited = useContext(Services);
  return (
    <Services.Provider
      value={{
        ...inherited,
        ...Object.fromEntries(
          Object.entries(services || {}).filter(
            ([, value]) => value !== undefined,
          ),
        ),
      }}
    >
      {children}
    </Services.Provider>
  );
}
export const useAuthoringServices = () => useContext(Services);

/** Replace one visible label through `editorPresentation.labels`. */
export function useLabel() {
  const labels = useContext(Services).editorPresentation?.labels;
  return (value: string) => {
    const next = labels?.[value];
    return typeof next === "string" && next.trim() ? next : value;
  };
}
/** Editor wording with host overrides applied. */
export function useEditorText() {
  const text = useContext(Services).editorPresentation?.text;
  const relabel = useLabel();
  return (key: EditorTextKey) => relabel(editorText(text, key));
}
